import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';

import $ from 'jquery';

import type { RecordData, Service } from 'webapp-types';
import { MetricDetailsTab } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';
import {
	arrayMove,
	PopularLazyLoadSelector,
	sortArrayBasedOnSubset,
	sortBy,
} from 'webapp/utils/common';

import makeArray from 'webapp/helpers/make-array';
import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import visibleCheckContainer, {
	containerEventName,
} from 'webapp/modifiers/visible-check-container';
import willDestroy from 'webapp/modifiers/will-destroy';
import CommonListLazyLoadComponent from 'webapp/components/common/list/lazy-load';
import CommonListLoaderComponent from 'webapp/components/common/list/loader';
import FilterLineProxyComponent from 'webapp/components/filter/line-proxy';
import FilterNoResultsComponent from 'webapp/components/filter/no-results';
import ForecastsMyGridItemComponent from 'webapp/components/forecasts/my/grid-item';

import FilterSortForecast from 'webapp/models-custom/filter-sort/forecast';
import type Forecast from 'webapp/models-custom/forecast';

interface Args {
	onOpenDetails?: (model: Forecast) => void;
}

export default class ForecastsMyGridComponent extends Component<Args> {
	@service('request') declare requestService: Service.Request;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('model-manager/forecasts') declare mmForecastsService: Service.ModelManager.Forecasts;
	@service('metric-details')
	declare metricDetailsService: Service.MetricDetails;
	@service('store') declare storeService: Service.Store;

	filterSort: FilterSortForecast;

	visibleCheckContainerNode: HTMLElement | null = null;
	sortableEl: JQuery | null = null;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		const filterSort = new FilterSortForecast({
			selectors: PopularLazyLoadSelector.grid,
			getAllItems: () => {
				return this.sortedModel;
			},
			afterFilter: () => {
				// for visible-check
				const node = this.visibleCheckContainerNode;
				if (node) {
					this.triggerContentChange(node);
				}
			},
		});

		this.filterSort = filterSort;
	}

	get model() {
		return this.mmForecastsService.forecastModels;
	}

	@cached
	get sortedModel() {
		return sortBy(this.model, 'order', 'numericId');
	}

	@action
	openDetails(model: Forecast) {
		if (this.args.onOpenDetails) {
			this.args.onOpenDetails(model);
		} else {
			this.metricDetailsService.openMetricDetailsDrawer({
				tab: MetricDetailsTab.FORECAST,
				forecast: { forecastId: model.id },
			});
		}
		model.trackEvent(TrackingEvent.FORECAST_DETAILS_VIEWED);
	}

	@action
	recalculate(model: Forecast) {
		this.mmForecastsService.recalculateForecast(model);
	}

	@action
	delete(model: Forecast) {
		this.mmForecastsService.deleteForecast(model);
	}

	@action
	triggerContentChange(node: HTMLElement): void {
		// for visible-check
		node.dispatchEvent(new Event(containerEventName));
	}

	@action
	setupView(node: HTMLElement): void {
		this.visibleCheckContainerNode = node;

		const itemSelectorString = this.filterSort.itemDomSelector,
			placeholderElementFn = (/*currentItem*/) => {
				return $(
					'<div class="drop-placeholder flex-item"><div><ui-icon name="DragAndDropArea" size="xl" class="bounce"></ui-icon><span>Drop here</span></div></div>',
				);
			};
		this.initSortable($(node), itemSelectorString, placeholderElementFn);
	}

	@action
	teardownView(): void {
		this.destroySortable();
	}

	// ---- sorting ----

	private initSortable(
		ctEl: JQuery,
		itemSelectorString: string,
		placeholderElementFn: (el: JQuery) => JQuery,
	): void {
		ctEl.sortable({
			handle: '.drag',
			containment: 'parent',
			placeholder: {
				element: placeholderElementFn,
				update: function () {
					return;
				},
			},
			forcePlaceholderSize: true,
			tolerance: 'pointer',
			items: itemSelectorString,
			start: (_e: JQuery.Event, ui: jQueryUIInstance) => {
				ui.item.find('.drag').attr('data-position', ui.item.index());
			},
			stop: (_e: JQuery.Event, ui: jQueryUIInstance) => {
				const handle = ui.item.find('.drag'),
					oldIndex = Number(handle.attr('data-position')),
					newIndex = ui.item.index(),
					id = handle.attr('data-id');

				if (oldIndex !== newIndex) {
					this.updateSortOrder(oldIndex, newIndex);
					this.analyticsService.event(TrackingEvent.FORECASTS_REORDER, {
						forecast_id: id,
						origin: 'forecasts',
					});
				}
			},
		});

		this.sortableEl = ctEl;
	}

	private destroySortable(): void {
		const el = this.sortableEl;
		if (el?.length && el.sortable('instance')) {
			el.sortable('destroy');
		}
	}

	private updateSortOrder(oldIndex: number, newIndex: number): void {
		const orderedIds = this.sortedModel.map((m) => m.numericId),
			filterOrderedIds = arrayMove(
				this.filterSort.results.map((m) => m.numericId),
				oldIndex,
				newIndex,
			),
			newSortOrder = sortArrayBasedOnSubset(orderedIds, filterOrderedIds);

		this.requestService
			.api<RecordData.Payload>('POST', '/forecasts/order', newSortOrder)
			.then((reply) => {
				this.storeService.pushPayload(reply);
			})
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));

		this.model.forEach((item) => {
			item.order = orderedIds.indexOf(item.numericId);
			item.applyBufferedChanges(['order']); // we won't be sending save
		});
	}
	<template>
		<FilterLineProxyComponent @model={{this.filterSort}} />

		<FilterNoResultsComponent @model={{this.filterSort}} />

		<CommonListLazyLoadComponent
			@classNames="lazy-grid"
			@filterSort={{this.filterSort}}
			@initialRowCount={{10}}
			@loadMoreRowCount={{5}}
			@animationProperty="id"
			@autoLoaded={{true}}
			@bottomDistance={{700}}
			@useScrollableContainer={{true}}
			@scrollableContainerSelector=".popup-type-forecasts-drawer .content"
			as |visibleItems exporter|
		>

			<ul
				class="flex-ct flex-ct--size-forecast"
				{{! don't add dynamic classes bcos of visible-check-container & sortable }}
				data-hidden={{if this.filterSort.noResults "true" "false"}}
				{{didInsert this.setupView}}
				{{visibleCheckContainer true}}
				{{didUpdate this.triggerContentChange visibleItems}}
				{{willDestroy this.teardownView}}
			>
				{{#each visibleItems as |item|}}
					<ForecastsMyGridItemComponent
						@model={{item}}
						@hideSort={{exporter.hideSort}}
						@openDetails={{this.openDetails}}
						@recalculate={{this.recalculate}}
						@delete={{this.delete}}
					/>
				{{/each}}

				{{! adding (max-in-line - 1) fake items so the incomplete last row items are not stretched too much }}
				{{#each (makeArray length=exporter.itemsPerRow)}}
					<li class="flex-item flex-item--fake"></li>
				{{/each}}

			</ul>
			{{#unless exporter.areAllItemsLoaded}}
				<CommonListLoaderComponent />
			{{/unless}}
		</CommonListLazyLoadComponent>
	</template>
}
